import React, { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  X,
  Search,
  MessageCircle,
  ChevronRight,
  ExternalLink,
} from "lucide-react";
import Api from "../redux/api";

interface Category {
  name: string;
  questions: string[];
}

interface PromptsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectQuestion: (question: string) => void;
  loader: boolean;
}

export default function PromptsModal({
  isOpen,
  onClose,
  onSelectQuestion,
  loader,
}: PromptsModalProps) {
  const [categories, setCategories] = useState<Category[]>([]);
  const [filtered, setFiltered] = useState<Category[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [expanded, setExpanded] = useState<string | null>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(searchQuery), 280);
    return () => clearTimeout(t);
  }, [searchQuery]);

  // Filter
  useEffect(() => {
    if (!debouncedQuery.trim()) {
      setFiltered(categories);
      setExpanded(null);
      return;
    }
    const q = debouncedQuery.toLowerCase();
    const result = categories
      .map((cat) => {
        const matchingQs = cat.questions.filter((qu) =>
          qu.toLowerCase().includes(q),
        );
        if (matchingQs.length > 0 || cat.name.toLowerCase().includes(q)) {
          return {
            ...cat,
            questions: cat.name.toLowerCase().includes(q)
              ? cat.questions
              : matchingQs,
          };
        }
        return null;
      })
      .filter((c): c is Category => c !== null);
    setFiltered(result);
    if (result.length > 0) setExpanded(result[0].name);
  }, [debouncedQuery, categories]);

  const fetchCategories = useCallback(async () => {
    try {
      setIsLoading(true);
      const res = await Api.get("/ai/app-settings", {
        params: { id: "web_prompt" },
      });
      if (res.data?.status && res.data?.data?.category) {
        setCategories(res.data.data.category);
        setFiltered(res.data.data.category);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      fetchCategories();
      setSearchQuery("");
      setExpanded(null);
      setTimeout(() => searchRef.current?.focus(), 350);
    }
  }, [isOpen, fetchCategories]);

  const handleSelect = (q: string) => {
    if (loader) return;
    onSelectQuestion(q);
    onClose();
  };

  const toggleCategory = (name: string) =>
    setExpanded((prev) => (prev === name ? null : name));

  const highlightText = (text: string, term: string) => {
    if (!term.trim()) return text;
    const regex = new RegExp(
      `(${term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`,
      "gi",
    );
    const parts = text.split(regex);
    return parts.map((part, i) =>
      regex.test(part) ? (
        <mark key={i} className="bg-[#3AC1F3]/20 text-[#3AC1F3] px-0.5 rounded">
          {part}
        </mark>
      ) : (
        part
      ),
    );
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-[150] bg-black/40 backdrop-blur-sm"
            onClick={onClose}
          />

          {/* Panel */}
          <motion.div
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            className="fixed right-0 top-0 h-full z-[251] w-[92vw] max-w-[440px] flex flex-col"
            style={{
              background: "rgba(255,255,255,0.92)",
              backdropFilter: "blur(32px) saturate(180%)",
              WebkitBackdropFilter: "blur(32px) saturate(180%)",
              borderLeft: "1px solid rgba(186,230,253,0.6)",
              boxShadow: "-20px 0 60px rgba(14,165,233,0.10), -4px 0 20px rgba(0,0,0,0.06)",
            }}
          >
            {/* Dark mode panel override */}
            <div
              className="hidden dark:block absolute inset-0 pointer-events-none rounded-none"
              style={{
                background: "rgba(13,15,26,0.88)",
                backdropFilter: "blur(32px) saturate(180%)",
                WebkitBackdropFilter: "blur(32px) saturate(180%)",
              }}
            />

            {/* Content (above the dark overlay) */}
            <div className="relative z-10 flex flex-col h-full">
              {/* Header */}
              <div className="px-4 md:px-5 pt-4 md:pt-5 pb-3 md:pb-4 border-b border-white/60 dark:border-white/[0.08]">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-xs font-semibold tracking-widest uppercase text-gray-400 dark:text-gray-500 mb-0.5">
                      Explore
                    </p>
                    <h2 className="text-lg font-bold text-gray-900 dark:text-white leading-tight">
                      All Prompts
                    </h2>
                  </div>
                  <motion.button
                    whileHover={{ scale: 1.08 }}
                    whileTap={{ scale: 0.92 }}
                    onClick={onClose}
                    className="w-9 h-9 rounded-xl flex items-center justify-center text-gray-500 dark:text-gray-400 hover:bg-black/[0.07] dark:hover:bg-white/[0.10] hover:text-gray-800 dark:hover:text-white transition-all duration-200"
                  >
                    <X size={18} />
                  </motion.button>
                </div>

                {/* Search */}
                <div className="relative">
                  <Search
                    size={14}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-600 pointer-events-none"
                  />
                  <input
                    ref={searchRef}
                    type="text"
                    placeholder="Search prompts..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-9 pr-9 py-2.5 rounded-xl bg-black/[0.05] dark:bg-white/[0.08] border border-white/60 dark:border-white/[0.08] focus:border-[#3AC1F3]/50 focus:ring-2 focus:ring-[rgba(58,193,243,0.15)] text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 text-sm outline-none transition-all"
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery("")}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-white transition-colors"
                    >
                      <X size={13} />
                    </button>
                  )}
                </div>

                {/* Result count */}
                <AnimatePresence>
                  {debouncedQuery.trim() && (
                    <motion.p
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      className="text-xs text-[#3AC1F3] mt-2 overflow-hidden"
                    >
                      {filtered.reduce((t, c) => t + c.questions.length, 0)}{" "}
                      results
                    </motion.p>
                  )}
                </AnimatePresence>
              </div>

              {/* Body */}
              <div className="flex-1 overflow-y-auto py-2 md:py-3 px-2 md:px-3">
                {isLoading ? (
                  <div className="space-y-2 mt-1">
                    {[...Array(7)].map((_, i) => (
                      <div
                        key={i}
                        className="h-11 rounded-xl bg-black/[0.06] dark:bg-white/[0.06] animate-pulse"
                        style={{ opacity: 1 - i * 0.1 }}
                      />
                    ))}
                  </div>
                ) : filtered.length > 0 ? (
                  <div className="space-y-1">
                    {filtered.map((cat, ci) => (
                      <motion.div
                        key={cat.name}
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: ci * 0.04 }}
                      >
                        {/* Category row */}
                        <button
                          onClick={() => toggleCategory(cat.name)}
                          className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-left transition-all duration-200 ${
                            expanded === cat.name
                              ? "bg-[rgba(58,193,243,0.10)] dark:bg-[rgba(58,193,243,0.12)]"
                              : "hover:bg-black/[0.05] dark:hover:bg-white/[0.07]"
                          }`}
                        >
                          <div
                            className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 transition-all duration-200"
                            style={
                              expanded === cat.name
                                ? {
                                    background:
                                      "linear-gradient(135deg, #3AC1F3 0%, #1e88e5 100%)",
                                  }
                                : undefined
                            }
                          >
                            <MessageCircle
                              size={13}
                              color={
                                expanded === cat.name ? "white" : undefined
                              }
                              className={
                                expanded === cat.name
                                  ? ""
                                  : "text-gray-400 dark:text-gray-500"
                              }
                            />
                          </div>
                          <span
                            className={`flex-1 text-sm font-medium truncate transition-colors ${
                              expanded === cat.name
                                ? "text-[#3AC1F3]"
                                : "text-gray-700 dark:text-gray-300"
                            }`}
                          >
                            {cat.name}
                          </span>
                          <span
                            className={`text-xs px-1.5 py-0.5 rounded-md font-semibold transition-all ${
                              expanded === cat.name
                                ? "bg-[rgba(58,193,243,0.2)] text-[#3AC1F3]"
                                : "bg-black/[0.07] dark:bg-white/[0.09] text-gray-500 dark:text-gray-500"
                            }`}
                          >
                            {cat.questions.length}
                          </span>
                          <motion.div
                            animate={{ rotate: expanded === cat.name ? 90 : 0 }}
                            transition={{ duration: 0.2 }}
                          >
                            <ChevronRight
                              size={14}
                              className={`transition-colors ${
                                expanded === cat.name
                                  ? "text-[#3AC1F3]"
                                  : "text-gray-400 dark:text-gray-600"
                              }`}
                            />
                          </motion.div>
                        </button>

                        {/* Questions */}
                        <AnimatePresence>
                          {expanded === cat.name && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: "auto", opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.25, ease: "easeInOut" }}
                              className="overflow-hidden"
                            >
                              <div className="ml-4 mt-1 mb-2 pl-3 border-l-2 border-[rgba(58,193,243,0.25)] space-y-0.5">
                                {cat.questions.map((q, qi) => (
                                  <motion.button
                                    key={qi}
                                    initial={{ opacity: 0, x: -6 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: qi * 0.035 }}
                                    onClick={() => handleSelect(q)}
                                    disabled={loader}
                                    className={`w-full text-left py-2 px-3 rounded-lg text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-black/[0.05] dark:hover:bg-white/[0.08] transition-all leading-relaxed ${
                                      loader
                                        ? "opacity-50 cursor-not-allowed"
                                        : "cursor-pointer"
                                    }`}
                                  >
                                    {highlightText(q, debouncedQuery)}
                                  </motion.button>
                                ))}
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </motion.div>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-16 text-center">
                    <div
                      className="w-12 h-12 rounded-2xl flex items-center justify-center mb-3"
                      style={{ background: "rgba(58,193,243,0.1)" }}
                    >
                      <MessageCircle size={22} className="text-[#3AC1F3]" />
                    </div>
                    <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                      {searchQuery.trim()
                        ? "No prompts found"
                        : "No prompts available"}
                    </p>
                    {searchQuery.trim() && (
                      <button
                        onClick={() => setSearchQuery("")}
                        className="mt-2 text-xs text-[#3AC1F3] hover:underline font-medium"
                      >
                        Clear search
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
